import React from "react";
import { 
  FiX, 
  FiClock, 
  FiDollarSign, 
  FiCheckCircle, 
  FiXCircle, 
  FiUser, 
  FiCalendar, 
  FiTag,
  FiTrendingUp,
  FiAward,
  FiBookOpen,
  FiVideo,
  FiPlay,
  FiEye,
  FiDownload,
  FiShare2,
  FiStar,
  FiUsers,
  FiBarChart2
} from "react-icons/fi";
import { 
  MdOutlineClose, 
  MdOutlineCheckCircle,
  MdOutlineAccessTime,
  MdOutlineAttachMoney,
  MdOutlinePerson,
  MdOutlineCalendarToday,
  MdOutlineCategory,
  MdOutlineVerified,
  MdOutlineCancel,
  MdOutlinePending,
  MdOutlineLocalOffer,
  MdOutlineCampaign,
  MdOutlinePlayCircle
} from "react-icons/md";
import { 
  IoCloseOutline, 
  IoCheckmarkCircle,
  IoTimeOutline,
  IoPersonOutline,
  IoCashOutline,
  IoCalendarOutline,
  IoPricetagOutline,
  IoPlayCircleOutline,
  IoVideocamOutline
} from "react-icons/io5";
import {
  FaClock,
  FaDollarSign,
  FaCheckCircle,
  FaTimesCircle,
  FaUser,
  FaCalendarAlt,
  FaBullhorn,
  FaTag,
  FaVideo,
  FaPlay,
  FaStar,
  FaUsers
} from "react-icons/fa";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const InfoRow = ({ icon: Icon, label, value, color = "text-indigo-400" }) => (
  <div className="flex items-start gap-3 group hover:bg-white/50 p-2 rounded-lg transition-colors duration-200">
    <span className={`mt-0.5 ${color} shrink-0`}>
      {Icon && <Icon className="w-4 h-4" />}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold leading-none mb-0.5">
        {label}
      </p>
      <p className="text-sm text-slate-700 font-medium break-words">
        {value ?? "—"}
      </p>
    </div>
  </div>
);

const Badge = ({ children, color = "indigo", icon: Icon }) => {
  const map = {
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-rose-50 text-rose-600 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    gray: "bg-slate-50 text-slate-500 border-slate-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${map[color] || map.gray}`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl bg-slate-50/80 border border-slate-200 p-4 space-y-3 hover:bg-slate-50 transition-colors duration-200">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-[#715EFE]" />}
      <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400">
        {title}
      </h4>
    </div>
    {children}
  </div>
);

const ViewAds = ({ onClose, data }) => {
  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
        <div className="bg-white rounded-2xl shadow-2xl p-10 text-center max-w-sm w-full animate-in zoom-in-95 duration-200">
          <div className="p-4 bg-rose-50 rounded-full w-fit mx-auto mb-4">
            <FiXCircle className="text-rose-400 text-4xl" />
          </div>
          <p className="text-slate-600 font-medium">No ad data available.</p>
          <button
            onClick={onClose}
            className="mt-5 px-6 py-2.5 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700 transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const course = data?.courseId;
  const plan = data?.planId;
  const user = data?.requestedBy || data?.user;
  const isApproved = data?.status === "approved" || data?.status === "active";

  const statusConfig = {
    approved: { color: "green", icon: FiCheckCircle, label: "Approved" },
    active: { color: "green", icon: FiCheckCircle, label: "Active" },
    pending: { color: "amber", icon: FiClock, label: "Pending" },
    rejected: { color: "red", icon: FiXCircle, label: "Rejected" },
    expired: { color: "gray", icon: FiClock, label: "Expired" },
  };
  const status = statusConfig[data?.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#855DF6] via-[#7B5CF6] to-[#715EFE] px-6 pt-5 pb-5 relative shrink-0">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors duration-200 p-1 rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            <FiX className="w-6 h-6" />
          </button>
          
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge color="purple" icon={FiTag}>
              Advertisement
            </Badge>
            <Badge color={status.color} icon={StatusIcon}>
              {status.label}
            </Badge>
            {data?.isDisable && (
              <Badge color="red" icon={FiXCircle}>Disabled</Badge>
            )}
          </div>
          
          <h2 className="text-white font-bold text-xl leading-tight line-clamp-2">
            {course?.title || data?.title || "Untitled Ad"}
          </h2>
          {course?.desc && (
            <p className="text-white/70 text-sm mt-1 line-clamp-2">
              {course.desc}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 flex-1 overflow-y-auto">
          {/* Thumbnail */}
          {(course?.thumbUrl || data?.imageUrl) && (
            <div className="rounded-xl overflow-hidden shadow-md border border-slate-200 bg-slate-100">
              <img
                src={course?.thumbUrl || data?.imageUrl}
                alt={course?.title || data?.title || "Thumbnail"}
                className="w-full h-56 object-cover hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          {/* User Info */}
          {user && (
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 hover:bg-slate-100 transition-colors duration-200">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center shrink-0 border-2 border-white shadow-md">
                {user?.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={user?.fullName || "User"}
                    className="w-full h-full rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentElement.innerHTML = `<span class="text-[#715EFE] font-bold text-sm">${(user?.fullName?.charAt(0) || 'U').toUpperCase()}</span>`;
                    }}
                  />
                ) : (
                  <span className="text-[#715EFE] font-bold text-sm">
                    {(user?.fullName?.charAt(0) || 'U').toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  Requested by
                </p>
                <p className="text-sm font-semibold text-slate-700 truncate">
                  {user?.fullName || "—"}
                </p>
              </div>
              {user?.createdAt && (
                <p className="text-xs text-slate-400 shrink-0">
                  Joined {fmt(new Date(user.createdAt).toISOString())}
                </p>
              )}
            </div>
          )}

          {/* Course & Plan Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Course Details */}
            <Section title="Course Details" icon={FiBookOpen}>
              <InfoRow
                icon={FiClock}
                label="Duration"
                value={course?.duration ? `${course.duration?.toFixed(2)} min` : "—"}
              />
              <InfoRow
                icon={FiDollarSign}
                label="Price"
                value={course?.price != null ? (course.price === 0 ? "Free" : `$${course.price}`) : "—"}
                color="text-emerald-500"
              />
              <InfoRow
                icon={FiTag}
                label="Purchases"
                value={course?.totalPurchase != null ? course.totalPurchase : "—"}
              />
              {course?.rating != null && (
                <InfoRow
                  icon={FiStar}
                  label="Rating"
                  value={`${course.rating} (${course.totalUserRating || 0} reviews)`}
                />
              )}
            </Section>

            {/* Boost Plan */}
            <Section title="Boost Plan" icon={FiTrendingUp}>
              {plan ? (
                <>
                  <InfoRow
                    icon={FiTag}
                    label="Plan"
                    value={plan?.title ?? "—"}
                  />
                  <InfoRow
                    icon={FiDollarSign}
                    label="Price"
                    value={plan?.price != null ? `$${plan.price}` : "—"}
                    color="text-emerald-500"
                  />
                  <InfoRow
                    icon={FiClock}
                    label="Duration"
                    value={plan?.durationInDays ? `${plan.durationInDays} days` : "—"}
                  />
                </>
              ) : (
                <p className="text-sm text-slate-400 italic">No plan info.</p>
              )}
            </Section>
          </div>

          {/* Timeline */}
          <Section title="Timeline" icon={FiCalendar}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoRow
                icon={FiCheckCircle}
                label="Approved At"
                value={fmt(data?.approveAt)}
                color="text-emerald-500"
              />
              <InfoRow
                icon={FiClock}
                label="Expires At"
                value={fmt(data?.expireAt)}
                color="text-rose-500"
              />
              {data?.startDate && (
                <InfoRow
                  icon={FiCalendar}
                  label="Start Date"
                  value={fmt(data?.startDate)}
                />
              )}
              {data?.endDate && (
                <InfoRow
                  icon={FiCalendar}
                  label="End Date"
                  value={fmt(data?.endDate)}
                />
              )}
            </div>
          </Section>

          {/* Video Preview */}
          {course?.videoUrl && (
            <Section title="Preview Video" icon={IoVideocamOutline}>
              <video
                controls
                className="w-full rounded-lg border border-slate-200 shadow-sm bg-black"
                style={{ maxHeight: "340px" }}
              >
                <source src={course.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </Section>
          )}

          {/* Footer */}
          <div className="flex justify-end pt-1">
            <button
              onClick={onClose}
              className="bg-gradient-to-r from-[#855DF6] to-[#715EFE] text-white text-sm font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAds;